/*
  window.MNWishlistStore: the theme's one wrapper around the wishlist app,
  Swish (window.swish, the Swish SDK loaded by the "Swish" app embed). Every
  heart, the header count, the wishlist drawer, the wishlist page and Add All
  to Cart read and write the wishlist through here, never through the app.

  What it adds on top of the SDK (checked against the SDK on the dev theme,
  @swishapp/sdk 0.151.0):

  - One copy of the saved items for the whole page, loaded once and kept
    current from the SDK's item-create / item-update / item-delete events, so
    no card or button fetches the wishlist itself. The API allows 120 requests
    a minute.
  - Optimistic changes: a save or removal shows straight away (a save takes a
    second or two) and is rolled back if the request fails.
  - Product handles. Swish items carry only product and variant ids; the
    wishlist page and the cart-page recommendations need the handle, so it is
    looked up through the SDK's Storefront client, once per product.
  - Combined listings. A parent (Aura Straight Leg Leggings - Taupe) sells its
    children's variants, so add() saves each variant against the child product
    that owns it, whichever page or card it was saved from. See ownerOf().
  - Hydration flavours are saved as two packs. A `pdp-link:<handle>` product is
    shown on its card as two packs at twice the price ("28 Pack"), and the
    container's buy box sells a flavour on its own as two packs, so add()
    saves it with quantity 2 rather than Swish's default of 1. See ownerOf().
  - removeByVariant() removes several items in one request, for Add All to Cart.

  Items are { itemId, productId, variantId, handle, product }. product is
  always null with Swish (kept for the shape the callers expect). handle is ''
  for a product the storefront can't load any more (deleted or unpublished),
  and null while it hasn't been looked up yet (or the lookup failed and is
  being retried), so a dropped request never reads as a deleted product.

  Loaded from layout/theme.liquid while Theme settings > Wishlist is on.
*/
(() => {
  if (window.MNWishlistStore) return;

  // How long to wait for the Swish SDK to load (blocked, or its embed switched
  // off) before failing instead of hanging.
  const READY_TIMEOUT_MS = 10000;
  // How long an optimistic change is kept if nothing ever confirms it.
  const OPTIMISTIC_TTL_MS = 15000;
  const PAGE_LIMIT = 50;
  const HANDLE_CACHE_KEY = 'mn_wishlist_handles';
  // A failed handle lookup is tried again after this long.
  const HANDLE_RETRY_MS = 3000;
  // v2: entries carry packs. Older entries in a session would read as 1 pack.
  const OWNER_CACHE_KEY = 'mn_wishlist_owners_v2';
  // How long a save waits for its owning product before saving without it.
  const OWNER_TIMEOUT_MS = 3000;

  let swish = null;
  // Saved items, or null before the first load.
  let saved = null;
  // Optimistic changes not yet confirmed.
  const pendingAdds = []; // { productId, variantId, handle, expires }
  const pendingRemoves = new Map(); // itemId -> expires
  const listeners = new Set();

  // loadedPromise resolves once a list has loaded; firstAttempt resolves with
  // whether the first load (and its retry) worked. A first load that failed is
  // retried in the background, and a later success still resolves loadedPromise.
  let resolveLoaded;
  const loadedPromise = new Promise((resolve) => {
    resolveLoaded = resolve;
  });
  let resolveFirstAttempt;
  const firstAttempt = new Promise((resolve) => {
    resolveFirstAttempt = resolve;
  });
  // Counts changes to `saved`, so a load that ran across a save or removal
  // knows its list is stale.
  let mutations = 0;
  let loaded = false;

  const toNumber = (value) => (value == null || value === '' ? null : Number(value));

  // ---------------------------------------------------------------------------
  // Product handles (productId -> handle), kept for the browser session
  // ---------------------------------------------------------------------------
  const handles = new Map();
  try {
    Object.entries(JSON.parse(window.sessionStorage.getItem(HANDLE_CACHE_KEY) || '{}')).forEach(([id, handle]) =>
      handles.set(Number(id), handle)
    );
  } catch (e) {
    // No storage: handles are looked up again.
  }

  function rememberHandle(productId, handle) {
    if (!productId || handles.get(productId) === handle) return;
    handles.set(productId, handle);
    try {
      window.sessionStorage.setItem(HANDLE_CACHE_KEY, JSON.stringify(Object.fromEntries(handles)));
    } catch (e) {
      // Storage full or blocked: the in-memory copy is enough.
    }
  }

  // Looks up the handles this page doesn't know yet. A product the storefront
  // no longer returns gets '' (the wishlist shows it as no longer available).
  async function resolveHandles(productIds) {
    const missing = [...new Set(productIds)].filter((id) => id && !handles.has(id));
    if (!missing.length) return;
    await Promise.all(
      missing.map(async (productId) => {
        try {
          // A lookup that never got an answer resolves to { errors } with no
          // data (it doesn't throw): leave the handle unknown so it's retried.
          // Only an answer of product: null means the product is gone.
          const response = await swish.storefront.loadProductHandle({ productId });
          if (!response || !response.data || response.errors) return;
          const product = response.data.product;
          rememberHandle(productId, (product && product.handle) || '');
        } catch (e) {
          // Leave it unknown (null); retryMissingHandles tries again.
        }
      })
    );
  }

  // ---------------------------------------------------------------------------
  // The product that owns a variant (combined listings), kept for the session
  // ---------------------------------------------------------------------------
  // A combined-listing parent has no variants of its own: Regular / S on
  // "Aura Straight Leg Leggings - Taupe" belongs to "...Regular - Taupe". Every
  // heart passes the page or card product, so a save from a parent used to
  // store the parent's id with a child's variant, which the app, Tapcart and
  // Klaviyo can't match up. The Storefront API (2025-10 on) returns the owning
  // child for a parent's variants, and the product itself for any other.
  //
  // The same lookup reads the product's tags for how many packs a save is: a
  // `pdp-link:<handle>` product (a Hydration flavour) is two, everything else
  // one. LINKED_PDP_PRICE_MULTIPLIER in src/helpers/linkedPdp.ts and
  // card_price_multiplier in snippets/card-product.liquid show it at that many
  // packs; keep them in step.
  const OWNER_QUERY = 'query ($id: ID!) { node(id: $id) { ... on ProductVariant { product { id handle tags } } } }';
  const LINKED_PDP_PACKS = 2;
  const owners = new Map(); // variantId -> { productId, handle, packs }
  try {
    Object.entries(JSON.parse(window.sessionStorage.getItem(OWNER_CACHE_KEY) || '{}')).forEach(([id, owner]) =>
      owners.set(Number(id), owner)
    );
  } catch (e) {
    // No storage: owners are looked up again.
  }

  function rememberOwner(variantId, owner) {
    owners.set(variantId, owner);
    try {
      window.sessionStorage.setItem(OWNER_CACHE_KEY, JSON.stringify(Object.fromEntries(owners)));
    } catch (e) {
      // Storage full or blocked: the in-memory copy is enough.
    }
  }

  // Resolves to { productId, handle, packs }, or null when the lookup can't be
  // made or doesn't answer within OWNER_TIMEOUT_MS; the save then goes ahead
  // with the product it was given, as one pack. The time limit is a plain timer:
  // AbortSignal.timeout() is missing before Safari 16 (iOS 15), and aborting
  // through the live site's wrapped fetch logs an uncaught AbortError. A late
  // answer is still remembered for the next save.
  async function ownerOf(variantId) {
    if (!variantId) return null;
    if (owners.has(variantId)) return owners.get(variantId);
    const config = (window.MN && window.MN.shopify) || {};
    if (!config.storefrontAccessToken || !config.graphqlEndpoint) return null;
    let timer = null;
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), OWNER_TIMEOUT_MS);
    });
    try {
      return await Promise.race([lookUpOwner(variantId, config), timedOut]);
    } finally {
      clearTimeout(timer);
    }
  }

  async function lookUpOwner(variantId, config) {
    try {
      const response = await fetch(config.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
        },
        body: JSON.stringify({ query: OWNER_QUERY, variables: { id: `gid://shopify/ProductVariant/${variantId}` } }),
      });
      if (!response.ok) return null;
      const json = await response.json();
      const product = json && json.data && json.data.node && json.data.node.product;
      if (!product || !product.id) return null;
      const linked = (product.tags || []).some((tag) => /^pdp-link:\s*\S/i.test(String(tag).trim()));
      const owner = {
        productId: toNumber(String(product.id).split('/').pop()),
        handle: product.handle || '',
        packs: linked ? LINKED_PDP_PACKS : 1,
      };
      if (!owner.productId) return null;
      rememberOwner(variantId, owner);
      return owner;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  // The handle for a product: '' when the storefront no longer has it, null
  // when it isn't known yet.
  function handleFor(productId) {
    return handles.has(productId) ? handles.get(productId) : null;
  }

  function fromApi(raw) {
    const productId = toNumber(raw.productId);
    return {
      itemId: String(raw.id),
      productId,
      variantId: toNumber(raw.variantId) || null,
      handle: handleFor(productId),
      product: null,
    };
  }

  function sameLine(item, productId, variantId) {
    if (productId != null && item.productId !== Number(productId)) return false;
    return variantId == null || item.variantId === Number(variantId);
  }

  // Whether a saved item is the line an optimistic add stands in for. Variant
  // ids are unique store-wide, so they decide alone.
  function confirmsAdd(item, add) {
    if (add.variantId != null) return item.variantId === add.variantId;
    return sameLine(item, add.productId, null);
  }

  function dropExpired() {
    const now = Date.now();
    for (let i = pendingAdds.length - 1; i >= 0; i--) {
      if (pendingAdds[i].expires < now) pendingAdds.splice(i, 1);
    }
    pendingRemoves.forEach((expires, itemId) => {
      if (expires < now) pendingRemoves.delete(itemId);
    });
  }

  // Saved items with the optimistic changes applied.
  function items() {
    if (!saved) return [];
    dropExpired();
    const list = saved
      .filter((item) => !pendingRemoves.has(item.itemId))
      .map((item) => (item.handle != null ? item : { ...item, handle: handleFor(item.productId) }));
    pendingAdds.forEach((add) => {
      if (!list.some((item) => confirmsAdd(item, add))) {
        list.unshift({
          itemId: '',
          productId: add.productId,
          variantId: add.variantId,
          handle: add.handle,
          product: null,
        });
      }
    });
    return list;
  }

  function notify() {
    const current = items();
    listeners.forEach((listener) => {
      try {
        listener(current);
      } catch (e) {
        console.error('Wishlist: listener failed', e);
      }
    });
  }

  // Applies items the API returned or announced (created or changed).
  function upsert(rawItems) {
    if (!saved) return;
    mutations++;
    rawItems.forEach((raw) => {
      const item = fromApi(raw);
      const index = saved.findIndex((existing) => existing.itemId === item.itemId);
      if (index === -1) saved.unshift(item);
      else saved[index] = item;
      for (let i = pendingAdds.length - 1; i >= 0; i--) {
        if (confirmsAdd(item, pendingAdds[i])) pendingAdds.splice(i, 1);
      }
    });
  }

  function drop(itemIds) {
    if (!saved) return;
    mutations++;
    const ids = new Set(itemIds.map(String));
    saved = saved.filter((item) => !ids.has(item.itemId));
    ids.forEach((id) => pendingRemoves.delete(id));
  }

  // The API answers { data } on success and { error } for an HTTP error. A
  // request that never got an answer (offline, blocked) comes back as
  // { data: undefined } with no error at all, so a missing data counts as a
  // failure too: otherwise a removal lost to the network would look done.
  function unwrap(response) {
    if (!response || response.error || response.data === undefined) {
      const error = (response && response.error) || {};
      const failure = new Error(error.message || 'Wishlist request failed');
      failure.status = error.statusCode;
      throw failure;
    }
    return response.data;
  }

  async function fetchList() {
    const all = [];
    let page;
    do {
      const response = await swish.api.items.list(page ? { limit: PAGE_LIMIT, page } : { limit: PAGE_LIMIT });
      const data = unwrap(response);
      all.push(...(data || []));
      page = response.pageInfo && response.pageInfo.next;
    } while (page);
    return all;
  }

  // Replaces `saved` with a fresh list. A save or removal that lands while the
  // list is in flight makes it stale, so it is fetched again (once).
  async function loadAll() {
    for (let attempt = 0; attempt < 2; attempt++) {
      const before = mutations;
      const all = await fetchList();
      await resolveHandles(all.map((raw) => toNumber(raw.productId)));
      if (mutations !== before && attempt === 0) continue;
      saved = all.map(fromApi);
      lastLoadAt = Date.now();
      mutations++;
      retryMissingHandles();
      return;
    }
  }

  // Lookups that failed (a dropped connection) are tried once more.
  let handleRetryTimer = null;
  function retryMissingHandles() {
    if (!saved || handleRetryTimer) return;
    const missing = saved.filter((item) => item.handle == null).map((item) => item.productId);
    if (!missing.length) return;
    handleRetryTimer = setTimeout(async () => {
      handleRetryTimer = null;
      await resolveHandles(missing);
      saved = saved.map((item) => (item.handle == null ? { ...item, handle: handleFor(item.productId) } : item));
      notify();
    }, HANDLE_RETRY_MS);
  }

  let reloading = null;
  function reload() {
    if (!reloading) {
      reloading = loadAll()
        .then(() => notify())
        .catch((e) => console.error('Wishlist: load failed', e))
        .finally(() => {
          reloading = null;
        });
    }
    return reloading;
  }

  // ---------------------------------------------------------------------------
  // Connecting to the SDK
  // ---------------------------------------------------------------------------
  async function connect(app) {
    if (swish || !app) return;
    swish = app;

    swish.events.subscribe(['item-create', 'item-update'], (event) => {
      const raw = event.detail;
      if (!raw || !saved) return;
      const productId = toNumber(raw.productId);
      resolveHandles([productId]).then(() => {
        upsert([raw]);
        notify();
      });
    });
    swish.events.subscribe('item-delete', (event) => {
      const removed = [].concat(event.detail || []).map((raw) => raw.id);
      if (!removed.length || !saved) return;
      drop(removed);
      notify();
    });
    // Signing in or out swaps the shopper's wishlist (a guest's saves are
    // merged into the account). The SDK also fires this while it sets up the
    // first token, before the first load, which needs no reload.
    let tokenTimer = null;
    swish.events.subscribe('token-update', () => {
      if (!saved) return;
      clearTimeout(tokenTimer);
      tokenTimer = setTimeout(reload, 300);
    });

    // Returning to the tab reloads a list older than a minute: saves made in
    // another tab show up, and so does a list an earlier load missed.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && saved !== null && Date.now() - lastLoadAt > REFRESH_ON_RETURN_MS) {
        reload();
      }
    });

    if (await tryLoad()) {
      recheckEmptyList();
      return;
    }
    // One retry for an HTTP error (an outage, or rate limited).
    await new Promise((resolve) => setTimeout(resolve, 1500));
    if (await tryLoad()) {
      recheckEmptyList();
      return;
    }
    // Still failing (an outage, or rate limited): ready() answers false so the
    // page shows its error rather than an empty wishlist, and the load keeps
    // retrying in the background, now and whenever the tab comes back.
    resolveFirstAttempt(false);
    retryInBackground();
  }

  // The SDK answers a list request that never reached Swish (offline, a
  // dropped connection) with a normal empty page rather than an error, so an
  // empty first load for a shopper who has saved before is checked once more.
  function recheckEmptyList() {
    if (!saved || saved.length) return;
    let hasSession = false;
    try {
      hasSession = Object.keys(window.localStorage).some((key) => key.startsWith('swish-session'));
    } catch (e) {
      // No storage, no session to recheck.
    }
    if (hasSession) setTimeout(reload, EMPTY_RECHECK_MS);
  }

  async function tryLoad() {
    try {
      await loadAll();
      loaded = true;
      resolveLoaded();
      resolveFirstAttempt(true);
      notify();
      return true;
    } catch (e) {
      console.error('Wishlist: load failed', e);
      return false;
    }
  }

  const EMPTY_RECHECK_MS = 5000;
  const REFRESH_ON_RETURN_MS = 60000;
  let lastLoadAt = 0;
  const BACKGROUND_RETRY_MS = 15000;
  const BACKGROUND_RETRIES = 4;
  function retryInBackground() {
    let tries = 0;
    let timer = null;
    const attempt = async () => {
      clearTimeout(timer);
      if (saved !== null || tries >= BACKGROUND_RETRIES) return;
      tries++;
      if (!(await tryLoad())) timer = setTimeout(attempt, BACKGROUND_RETRY_MS);
    };
    timer = setTimeout(attempt, BACKGROUND_RETRY_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') attempt();
    });
  }

  if (window.swish && window.swish.api) {
    connect(window.swish);
  } else {
    document.addEventListener('swish-ready', () => connect(window.swish), { once: true });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  // Resolves true once a list has loaded; false if the first load failed (an
  // outage) or after timeoutMs (the SDK blocked, or its embed switched off).
  function ready(timeoutMs) {
    if (loaded) return Promise.resolve(true);
    const settled = Promise.race([loadedPromise.then(() => true), firstAttempt]);
    if (!timeoutMs) return settled;
    return Promise.race([settled, new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs))]);
  }

  // Resolves once a list has loaded, however long that takes (never, if the
  // SDK never arrives). A page that gave up on ready(timeoutMs) and showed a
  // message waits on this to fill in when the background retry lands.
  function whenLoaded() {
    return loadedPromise;
  }

  async function whenReadyOrFail() {
    if (!(await ready(READY_TIMEOUT_MS))) throw new Error('Wishlist is unavailable right now');
  }

  function has(productId, variantId) {
    return items().some((item) => sameLine(item, productId, variantId));
  }

  function findItem(productId, variantId) {
    return items().find((item) => item.itemId && sameLine(item, productId, variantId));
  }

  // productHandle is optional with Swish (it saves by id); when given it saves
  // a lookup on the wishlist page. productId is the page or card product; the
  // item is saved against the product that owns the variant (see ownerOf), so
  // a combined-listing parent saves its child. ownerKnown says productId
  // already owns the variant (a cart line, or a card variant that came with its
  // product), so only the lookup's pack count is used.
  async function add({ productHandle, productId, variantId, ownerKnown = false }) {
    const pending = {
      productId: toNumber(productId),
      variantId: toNumber(variantId),
      handle: productHandle || '',
      expires: Date.now() + OPTIMISTIC_TTL_MS,
    };
    if (!pending.productId) throw new Error('Wishlist: productId is required');
    if (productHandle) rememberHandle(pending.productId, productHandle);
    pendingAdds.push(pending);
    notify();
    try {
      const [owner] = await Promise.all([ownerOf(pending.variantId), whenReadyOrFail()]);
      if (owner && !ownerKnown && owner.productId !== pending.productId) {
        pending.productId = owner.productId;
        pending.handle = owner.handle;
        if (owner.handle) rememberHandle(owner.productId, owner.handle);
      }
      const body = { productId: pending.productId };
      if (pending.variantId) body.variantId = pending.variantId;
      if (owner && owner.packs > 1) body.quantity = owner.packs;
      const item = unwrap(await swish.api.items.create(body));
      upsert([item]);
      notify();
    } catch (error) {
      const index = pendingAdds.indexOf(pending);
      if (index !== -1) pendingAdds.splice(index, 1);
      notify();
      throw error;
    }
    const detail = { productId: pending.productId, variantId: pending.variantId, handle: handleFor(pending.productId) || '' };
    document.dispatchEvent(new CustomEvent('wishlistItemAdded', { detail }));
    // Read by the GTM listener in layout/theme.liquid.
    document.dispatchEvent(new CustomEvent('mn:wishlist-add', { detail }));
  }

  async function remove(itemId) {
    if (!itemId) return;
    pendingRemoves.set(itemId, Date.now() + OPTIMISTIC_TTL_MS);
    notify();
    try {
      await whenReadyOrFail();
      unwrap(await swish.api.items.delete([itemId]));
      drop([itemId]);
      notify();
    } catch (error) {
      // 404: Swish has no such item in the wishlist (removed in another tab,
      // or a cart save that was never in it), so it's gone either way.
      if (error.status === 404) {
        drop([itemId]);
        notify();
        return;
      }
      pendingRemoves.delete(itemId);
      notify();
      throw error;
    }
  }

  // Removes every saved line for the given variant ids in one request. Used by
  // Add All to Cart. Resolves once the request has answered; never rejects.
  async function removeByVariant(variantIds) {
    if (!(await ready(READY_TIMEOUT_MS))) return;
    const wanted = new Set((variantIds || []).map(String));
    const targets = items().filter((item) => item.itemId && wanted.has(String(item.variantId)));
    if (!targets.length) return;

    const ids = targets.map((item) => item.itemId);
    const expires = Date.now() + OPTIMISTIC_TTL_MS;
    ids.forEach((id) => pendingRemoves.set(id, expires));
    notify();
    try {
      unwrap(await swish.api.items.delete(ids));
      drop(ids);
    } catch (e) {
      console.error('Wishlist: remove failed', e);
      ids.forEach((id) => pendingRemoves.delete(id));
    }
    notify();
  }

  async function changeVariant(itemId, variantId) {
    await whenReadyOrFail();
    const item = unwrap(await swish.api.items.updateById(itemId, { variantId: Number(variantId) }));
    upsert([item]);
    notify();
    return item;
  }

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  window.MNWishlistStore = {
    ready,
    whenLoaded,
    items,
    has,
    findItem,
    count: () => items().length,
    add,
    remove,
    removeByVariant,
    changeVariant,
    subscribe,
  };

  // For scripts that can run before this one (the React bundles, inserted by
  // snippets/feature-script.liquid): see src/helpers/wishlistStore.ts.
  document.dispatchEvent(new Event('mn:wishlist-store'));
})();
